/**
 * Guard against controlled checkbox flicker: user click updates local UI, then a
 * concurrent EdiTable cell-blur onChange/refill briefly pushes a stale parent
 * modelValue and the watch would reset the box.
 */

import { coerceStateBoolean } from '@/common/utils/resolveActionPageState'

export const CHECKBOX_USER_EMIT_GUARD_MS = 2000

/** Binary checkbox: UniData may send `"false"` / `"true"` strings. */
export function coerceCheckboxBinaryModelValue(value: unknown): boolean {
  if (value === undefined || value === null) return false
  return coerceStateBoolean(value) === true
}

export function isSelectedCheckboxRow(row: unknown): boolean {
  if (!row || typeof row !== 'object') return false
  const r = row as Record<string, unknown>
  const fields = r.fields as Record<string, { value?: unknown }> | undefined
  const rawValue = fields?.value?.value ?? (r.value as { value?: unknown } | undefined)?.value ?? r.value
  if (typeof rawValue === 'string') {
    const normalized = rawValue.trim().toLowerCase()
    return normalized === 'true' || normalized === '1'
  }
  return Boolean(rawValue)
}

export function getCheckboxRowId(row: unknown): unknown {
  if (!row || typeof row !== 'object') return null
  const r = row as Record<string, unknown>
  const fields = r.fields as Record<string, { value?: unknown }> | undefined
  return fields?.id?.value ?? (r.id as { value?: unknown } | undefined)?.value ?? r.id ?? null
}

/** Comparable snapshot of checkbox model (binary or multi-option table). */
export function normalizeCheckboxModelSnapshot(modelValue: unknown): string {
  if (modelValue && typeof modelValue === 'object' && Array.isArray((modelValue as { rows?: unknown }).rows)) {
    const rows = (modelValue as { rows: unknown[] }).rows
    const selected = rows.find((row) => isSelectedCheckboxRow(row))
    if (rows.length > 1) {
      return `id:${String(selected ? getCheckboxRowId(selected) : '')}`
    }
    return `bin:${isSelectedCheckboxRow(rows[0]) ? '1' : '0'}`
  }
  if (Array.isArray(modelValue)) {
    const selected = modelValue.find((row) => isSelectedCheckboxRow(row))
    if (modelValue.length > 1) {
      return `id:${String(selected ? getCheckboxRowId(selected) : modelValue[0] ?? '')}`
    }
    return `bin:${modelValue.some((row) => isSelectedCheckboxRow(row)) ? '1' : '0'}`
  }
  return `bin:${coerceCheckboxBinaryModelValue(modelValue) ? '1' : '0'}`
}

export function shouldApplyIncomingCheckboxModelValue(options: {
  incoming: unknown
  lastUserEmitted: unknown | null
  lastUserEmittedAt: number | null
  now?: number
  windowMs?: number
}): boolean {
  const { incoming, lastUserEmitted, lastUserEmittedAt } = options
  if (lastUserEmittedAt == null || lastUserEmitted === null || lastUserEmitted === undefined) {
    return true
  }
  const now = options.now ?? Date.now()
  const windowMs = options.windowMs ?? CHECKBOX_USER_EMIT_GUARD_MS
  if (now - lastUserEmittedAt > windowMs) {
    return true
  }
  return (
    normalizeCheckboxModelSnapshot(incoming) === normalizeCheckboxModelSnapshot(lastUserEmitted)
  )
}
